/**
 * RP80 / RP81 — « Ma semaine » du tableau de bord de l'architecte (fiche 01 ARC-R6).
 *
 * Par sujet : actions en retard, levées à vérifier, comptes rendus à relire, visas en
 * retard ou à viser, lots confiés à relire, comptes rendus à rédiger.
 * Par chantier : les mêmes compteurs, une ligne par chantier (le plus chargé d'abord).
 * Profil collaborateur : ses blocs (CR à rédiger, lots à rédiger, relances…).
 * Fluidité B2 — le même composant est affiché par le tableau de bord et par la page
 * « Ma semaine » du menu (une seule « Ma semaine ») ; `actions` ajoute un bouton par
 * ligne (ex. « Relancer »), `sansTitre` laisse le titre à la page.
 * Aucune donnée inventée : un bloc vide dit « Rien en attente ».
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, CalendarCheck } from '@phosphor-icons/react';
import { Skeleton } from '../ui/skeleton';
import { ETAPES_CHANTIER, etapeChantier } from '../../lib/libelles';
import MaSemaineBloc from './MaSemaineBloc';
import { COLONNES_PAR_CHANTIER, dateCourte } from './maSemaine';

const BLOCS_ARCHITECTE = ['a_trier', 'reponses_entreprises', 'desaccords', 'actions_en_retard', 'levees_a_verifier', 'cr_a_relire', 'visas_en_retard', 'visas_a_viser', 'lots_a_relire'];
const BLOCS_COLLABORATEUR = ['a_trier', 'cr_brouillons', 'reunions_sans_cr', 'cr_soumis', 'reponses_entreprises', 'desaccords', 'lots_confies', 'relances', 'actions_en_retard'];

const API_URL = process.env.REACT_APP_BACKEND_URL;

// B3 — récap du matin par e-mail (à trier, levées à vérifier, désaccords, retards), désactivable.
function RecapMatin() {
  const [actif, setActif] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/me/recap-matin`, { withCredentials: true })
      .then((r) => setActif(!!r.data?.actif)).catch(() => setActif(null));
  }, []);
  if (actif === null) return null;
  const basculer = async () => {
    try {
      const r = await axios.put(`${API_URL}/api/me/recap-matin`, { actif: !actif }, { withCredentials: true });
      setActif(!!r.data?.actif);
    } catch { /* réglage facultatif */ }
  };
  return (
    <label className="flex items-center gap-2 text-xs text-[#6B6B6B] cursor-pointer" data-testid="ma-semaine-recap-matin">
      <input type="checkbox" checked={actif} onChange={basculer} />
      Recevoir le récap du matin par e-mail
    </label>
  );
}

const libelleEtape = (c) => ETAPES_CHANTIER.find((e) => e.cle === etapeChantier(c))?.libelle || '';

export default function MaSemaineCabinet({ semaine, loading, actions = {}, sansTitre = false, max = 5 }) {
  const [vue, setVue] = useState('sujet');

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
        {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-40" />)}
      </div>
    );
  }
  if (!semaine) return null;

  const collaborateur = semaine.profil === 'collaborateur';
  const cles = collaborateur ? BLOCS_COLLABORATEUR : BLOCS_ARCHITECTE;
  const compteurs = semaine.compteurs || {};
  const aTraiter = cles.filter((k) => (compteurs[k] || 0) > 0);

  return (
    <section className="mb-8" data-testid="ma-semaine">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
        <div className={sansTitre ? 'sr-only' : ''}>
          <h2 className="text-xl text-[#2C2C2C] flex items-center gap-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
            <CalendarCheck size={22} className="text-[#C75B39]" /> Ma semaine
          </h2>
          <p className="text-sm text-[#6B6B6B]">
            Du {dateCourte(semaine.aujourd_hui)} au {dateCourte(semaine.fin_semaine)}
            {collaborateur ? ' — vos chantiers' : ' — tous les chantiers du cabinet'}
          </p>
        </div>
        <div className="flex flex-col items-start sm:items-end gap-2">
        <RecapMatin />
        <div className="flex items-center gap-1 bg-[#F5F5F3] p-1" role="tablist">
          {[['sujet', 'Par sujet'], ['chantier', 'Par chantier']].map(([k, lib]) => (
            <button
              key={k}
              role="tab"
              aria-selected={vue === k}
              onClick={() => setVue(k)}
              className={`px-3 py-1.5 text-sm ${vue === k ? 'bg-white text-[#2C2C2C] font-medium' : 'text-[#6B6B6B]'}`}
              data-testid={`ma-semaine-vue-${k}`}
            >
              {lib}
            </button>
          ))}
        </div>
        </div>
      </div>

      {vue === 'sujet' ? (
        aTraiter.length === 0 ? (
          <div className="border border-[#E0E0DB] bg-white text-center py-8 text-sm text-[#6B6B6B]" data-testid="ma-semaine-rien">
            Rien en retard ni en attente cette semaine.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {aTraiter.map((k) => (
              <MaSemaineBloc key={k} cle={k} items={semaine[k] || []} total={compteurs[k]} max={max} action={actions[k]} />
            ))}
          </div>
        )
      ) : (
        <div className="border border-[#E0E0DB] bg-white overflow-x-auto" data-testid="ma-semaine-par-chantier">
          {(semaine.par_chantier || []).length === 0 ? (
            <p className="text-sm text-[#6B6B6B] text-center py-8">Aucun chantier n'a de point en attente cette semaine.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAF8] border-b border-[#E0E0DB]">
                <tr>
                  <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-[#6B6B6B]">Chantier</th>
                  <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-[#6B6B6B]">Étape</th>
                  {COLONNES_PAR_CHANTIER.map((c) => (
                    <th key={c.cle} className="text-center py-2 px-2 text-xs uppercase tracking-wider text-[#6B6B6B]">{c.libelle}</th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F0F0EC]">
                {semaine.par_chantier.map((l) => (
                  <tr key={l.chantier_id} className="hover:bg-[#FAFAF9]" data-testid={`ma-semaine-chantier-${l.chantier_id}`}>
                    <td className="py-2 px-3 font-medium text-[#2C2C2C]">
                      <Link to={`/chantiers/${l.chantier_id}`} className="hover:text-[#C75B39]">{l.chantier_name}</Link>
                    </td>
                    <td className="py-2 px-3 text-xs text-[#1E3A5F]">{libelleEtape(l)}</td>
                    {COLONNES_PAR_CHANTIER.map((c) => {
                      const n = l.compteurs?.[c.cle] || 0;
                      return (
                        <td key={c.cle} className={`py-2 px-2 text-center ${n ? 'font-bold text-[#2C2C2C]' : 'text-[#C9C9C4]'}`}>
                          {n || '—'}
                        </td>
                      );
                    })}
                    <td className="py-2 px-3 text-right">
                      <Link to={`/chantiers/${l.chantier_id}`} aria-label={`Ouvrir ${l.chantier_name}`}>
                        <ArrowRight size={14} className="text-[#9A9A9A]" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section>
  );
}
